import { useCallback, useEffect } from 'react';
import styled, { css } from 'styled-components';
import useImpressionTracking from 'src/hooks/useImpressionTracking';
import Layout from '../Layout';
import useChatList from './hooks/useChatList';
import useCompletion from './hooks/useCompletion';
import Question from '../Question';
import useTransition from '../../hooks/useTransition';
import { trackInterviewViewed } from '../../analytics';
import Completion from './Completion';
import useInterview from '../../hooks/useInterview';
import usePolarisActor from 'src/hooks/usePolarisActor';

import type { IMiniInterviewProps } from 'src/packages/polaris/types/interviews';

const Container = styled.div<{ isEmbedded?: boolean }>`
  .BuoyInterviews__Question__parsedQuestion strong {
    font-weight: normal;
  }
  // Override overflow behavior for embedded TG interview
  ${({ isEmbedded }) =>
    isEmbedded &&
    css`
      .bp-overflow-hidden {
        overflow: scroll;
      }
    `}
`;

export const TreatmentGuidanceInterviewContainer = ({
  isEmbedded = false,
  slug,
  onQuestionDisplay,
  onQuestionSubmit,
  polarisProps,
}: Pick<
  IMiniInterviewProps,
  'slug' | 'onQuestionDisplay' | 'onQuestionSubmit' | 'polarisProps' | 'isEmbedded'
>) => {
  const { showProgressBar = true } = polarisProps || {};
  const state = usePolarisActor()?.getSnapshot();
  const transition = useTransition();
  const chatList = useChatList();
  const completion = useCompletion();
  const interview = useInterview();
  const activeQuestion = chatList.items[chatList.active];
  const questions = state?.context.questions;

  useEffect(() => {
    // New question or result loaded
    onQuestionDisplay?.(chatList.active);
  }, [chatList.active, completion.results]);

  const handleRestart = useCallback(() => {
    transition({
      event: 'RESET',
    });
  }, [transition]);

  const handleGoBack = useCallback(() => {
    transition({
      event: 'BACK',
    });
  }, [transition]);

  const trackViewed = useCallback(() => {
    if (interview.token) {
      trackInterviewViewed(
        interview.token,
        state?.context.interviewType,
        isEmbedded ? 'form-embedded' : 'polaris-popup',
      );
    }
  }, [interview.token, isEmbedded, state?.context.interviewType]);

  const handleExpanded = useCallback(() => {
    trackViewed();
  }, [trackViewed]);

  const impressionRef = useImpressionTracking(() => {
    if (polarisProps?.displayPolarisUI || polarisProps?.defaultExpanded) {
      trackViewed();
    }
  });

  const showCompletionScreen =
    !!completion.results ||
    state?.matches('fetchingTreatmentGuidance') ||
    state?.matches('loadingInitializationData');

  return (
    <Container isEmbedded={isEmbedded} ref={impressionRef}>
      <Layout
        defaultExpanded={polarisProps?.defaultExpanded}
        handleExit={polarisProps?.onExit}
        handleGoBack={handleGoBack}
        isAccordionEmbeddedLayout={!!polarisProps}
        isEmbedded={isEmbedded}
        onExpanded={handleExpanded}
        showBackButton={chatList.active !== 0}
        showProgressBar={showProgressBar && !showCompletionScreen}
        // showRestartButton={chatList.active !== 0}
        title={polarisProps?.titleQuestion}
        triggerRestart={handleRestart}
      >
        {showCompletionScreen ? (
          <Completion triggerRestart={handleRestart} />
        ) : (
          <Question
            onQuestionSubmit={onQuestionSubmit}
            question={activeQuestion}
            slug={slug}
            totalQuestions={questions.length}
          />
        )}
      </Layout>
    </Container>
  );
};

export default TreatmentGuidanceInterviewContainer;
